import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import {
  Modal,
  Button,
  Checkbox,
  Layout,
  Headline,
  TextField,
} from '@folio/stripes/components';

const ColumnSelectionModal = ({
  open,
  onClose,
  onConfirm,
  availableColumns,
  columnMapping,
}) => {
  // Filter out UI-only columns that can't be exported
  const exportableColumns = availableColumns.filter((col) => col !== 'actions');

  const [selectedColumns, setSelectedColumns] = useState(
    exportableColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {}),
  );
  const [reportName, setReportName] = useState('circulation-log-report');

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = exportableColumns.every((col) => selectedColumns[col]);
    const newState = exportableColumns.reduce(
      (acc, col) => ({
        ...acc,
        [col]: !allSelected,
      }),
      {},
    );

    setSelectedColumns(newState);
  };

  const handleConfirm = () => {
    const selected = exportableColumns.filter((col) => selectedColumns[col]);

    onConfirm(selected, reportName);
  };

  const selectedCount = exportableColumns.filter(
    (col) => selectedColumns[col],
  ).length;
  const allSelected = exportableColumns.every((col) => selectedColumns[col]);

  return (
    <Modal
      open={open}
      label={
        <FormattedMessage id="ui-circulation-log.excel.columnSelection.title" />
      }
      onClose={onClose}
      size="medium"
    >
      <Layout className="padding-all-gutter">
        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <TextField
            label={
              <FormattedMessage id="ui-circulation-log.excel.columnSelection.reportName" />
            }
            placeholder={
              <FormattedMessage id="ui-circulation-log.excel.columnSelection.reportNamePlaceholder" />
            }
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
          />
        </div>
        <Headline size="medium" margin="none">
          <FormattedMessage id="ui-circulation-log.excel.columnSelection.subtitle" />
        </Headline>

        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <Checkbox
            checked={allSelected}
            onChange={handleSelectAll}
            label={
              <FormattedMessage id="ui-circulation-log.excel.columnSelection.selectAll" />
            }
          />
        </div>

        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            padding: '1rem',
          }}
        >
          {exportableColumns.map((columnKey) => (
            <div key={columnKey} style={{ marginBottom: '0.5rem' }}>
              <Checkbox
                checked={selectedColumns[columnKey]}
                onChange={() => handleColumnToggle(columnKey)}
                label={columnMapping[columnKey] || columnKey}
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <FormattedMessage
            id="ui-circulation-log.excel.columnSelection.selectedCount"
            values={{ count: selectedCount, total: exportableColumns.length }}
          />
        </div>

        <div
          style={{
            marginTop: '2rem',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
          }}
        >
          <Button onClick={onClose}>
            <FormattedMessage id="ui-circulation-log.excel.columnSelection.cancel" />
          </Button>
          <Button
            buttonStyle="primary"
            onClick={handleConfirm}
            disabled={selectedCount === 0}
          >
            <FormattedMessage id="ui-circulation-log.excel.columnSelection.export" />
          </Button>
        </div>
      </Layout>
    </Modal>
  );
};

ColumnSelectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  availableColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  columnMapping: PropTypes.object.isRequired,
};

export default ColumnSelectionModal;
